import { GoalPeriodStatus } from "../enums/goal-period-status.enum";
import { ReviewPeriodStatusEnum } from "../enums/review-period-status.enum";
import { ActionProgress } from "../models/action-progress.model";
import { Goal } from "../models/goal.model";
import { GoalPeriod } from "../models/goalperiod.model";
import { ReviewPeriodCustomGoal } from "../models/review-period-custom-goal.model";
import { State } from "../models/state.model";
import { ReviewPeriodHelper } from "./review-period.helper";
import { EmployeeHelper } from "./employee.helper";

export abstract class GoalPermissionsHelper {

    public static isSelfFromPeriod(currentPeriod: GoalPeriod, state: State): boolean {
        return state?.me.value?.employee?.id === currentPeriod?.employeeId;
    }

    public static hasRightBasedOnSettings(forLeader: boolean, forSelf: boolean, forAssistant: boolean, currentPeriod: GoalPeriod, state: State): boolean {
        let hasRight = (forLeader && currentPeriod.isCurrentUserBossOfTargetEmployee) ||
            (forSelf && GoalPermissionsHelper.isSelfFromPeriod(currentPeriod, state)) ||
            (forAssistant && state.isAssistant);
        return hasRight;
    }

    public static isAllowByPeriod(currentPeriod: GoalPeriod): boolean {
        const notAllowed = !currentPeriod || 
            EmployeeHelper.isEmployeeInactive(currentPeriod) || 
            !ReviewPeriodHelper.isEditableByDate(currentPeriod.reviewPeriod) ||
            currentPeriod.reviewPeriod.status === ReviewPeriodStatusEnum.Closed;

        return !notAllowed;
    }

    // ----- 

    public static canComment(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (currentPeriod.reviewPeriod.status === ReviewPeriodStatusEnum.Closed) return false;
        if (state?.isAdmin || state?.isSuperAdmin) 
        {
            let val: any = state?.me.value;
            return !!val.companyId;
        }

        if (state?.isAssistant && currentPeriod.companyId !== (state?.me.value as any).companyId) 
            return false;

        const isInAllowedState = [GoalPeriodStatus.Editing, GoalPeriodStatus.SelfAssessment, GoalPeriodStatus.LeaderAssessment].includes(currentPeriod?.status);
        return isInAllowedState;
    }

    public static canCommentToCustomGoals(currentPeriod: GoalPeriod, state: State, customGoal: ReviewPeriodCustomGoal): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state?.isAdmin || state?.isSuperAdmin) 
        {
            let val: any = state?.me.value;
            return !!val.companyId;
        }

        if (currentPeriod.status === GoalPeriodStatus.Editing && GoalPermissionsHelper.isSelfFromPeriod(currentPeriod, state) && currentPeriod.reviewPeriod.reviewPeriodGoalSettings?.notAllowGoalChangesInFirstStateForSelf) {
            return false;
        }

        const isInAllowedState = [GoalPeriodStatus.Editing, GoalPeriodStatus.SelfAssessment, GoalPeriodStatus.LeaderAssessment].includes(currentPeriod?.status);
        if (!isInAllowedState) return false;

        let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
            customGoal.isAllowChangeForLeader,
            customGoal.isAllowChangeForSelf,
            customGoal.isAllowChangeForAssistant,
            currentPeriod,
            state
        );
        
        return hasRight;
    }

    // Goal

    // add new to: Text based or Numeric Goal
    public static canGoalActionAddNew(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;
       
        if (state.isAdmin || state.isSuperAdmin) return true;

        if (currentPeriod.status != GoalPeriodStatus.Editing) return false;

        let settings = currentPeriod.reviewPeriod.reviewPeriodGoalSettings;
        let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
            settings.isAllowChangeGoalTextForLeader,
            settings.isAllowChangeGoalTextForSelf,
            settings.isAllowChangeGoalTextForAssistant, currentPeriod, state);

        return hasRight;
    }

    public static canGoalFormEdit(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        if (GoalPermissionsHelper.isSelfFromPeriod(currentPeriod, state)) {
            if (currentPeriod.status === GoalPeriodStatus.Editing)
                return true;
        }
        else if (currentPeriod.isCurrentUserBossOfTargetEmployee) {
            if (currentPeriod.status === GoalPeriodStatus.Editing)
                return true;
        }
        else if (state.isAssistant)
        {
            return false;
        }

        return false;
    }

    public static canEditDeadline(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        let settings = currentPeriod.reviewPeriod.reviewPeriodGoalSettings;

        let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
            settings.isAllowChangeDeadlineForLeader, 
            settings.isAllowChangeDeadlineForSelf, 
            settings.isAllowChangeDeadlineForAssistant, currentPeriod, state);

        return currentPeriod?.status === GoalPeriodStatus.Editing && hasRight;
    }

    public static canEditWeight(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        let settings = currentPeriod.reviewPeriod.reviewPeriodGoalSettings;

        let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
            settings.isAllowChangeWeightForLeader, 
            settings.isAllowChangeWeightForSelf,
            settings.isAllowChangeWeightForAssistant, currentPeriod, state);

        const isStatusMatch = currentPeriod?.status === GoalPeriodStatus.Editing;

        return isStatusMatch && hasRight;
    }

    // Alap mezők szerkesztés jogosultsága, mint megnevezés, egység stb.
    public static canEditBasicFields(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        let settings = currentPeriod.reviewPeriod.reviewPeriodGoalSettings;
        let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
            settings.isAllowChangeGoalTextForLeader,
            settings.isAllowChangeGoalTextForSelf,
            settings.isAllowChangeGoalTextForAssistant, currentPeriod, state);

        return currentPeriod?.status === GoalPeriodStatus.Editing && hasRight;
    }

    public static canGoalActionSave(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        return currentPeriod?.status < GoalPeriodStatus.Finished;
    }

    public static canGoalDelete(currentPeriod: GoalPeriod, state: State, goal: Goal): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (goal == null) return false;
        if (state.isAdmin || state.isSuperAdmin) return true;

        if (currentPeriod?.status >= GoalPeriodStatus.SelfAssessment) {
            return false;
        }

        let settings = currentPeriod.reviewPeriod.reviewPeriodGoalSettings;
        let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
            settings.isAllowChangeGoalTextForLeader,
            settings.isAllowChangeGoalTextForSelf,
            settings.isAllowChangeGoalTextForAssistant, currentPeriod, state);

        return hasRight;
    }

    // Értékelő mezők szerkesztés jogosultsága
    public static canEditGoalRatings(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        return [GoalPeriodStatus.SelfAssessment, GoalPeriodStatus.LeaderAssessment].includes(currentPeriod?.status);
    }

    // Ez a goal View tételeken levő "Szerkeszés Értékelés" gomb kattinthatóságát szabályozza
    public static canGoalEdit(currentPeriod: GoalPeriod, goal: Goal, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (currentPeriod.status === GoalPeriodStatus.Editing && GoalPermissionsHelper.isSelfFromPeriod(currentPeriod, state) && currentPeriod.reviewPeriod.reviewPeriodGoalSettings?.notAllowGoalChangesInFirstStateForSelf) {
            return false;
        }

        return currentPeriod.status < GoalPeriodStatus.Finished;
    }

  // Goal Actions

  public static canActionSliderChange(currentPeriod: GoalPeriod, state: State, ap: ActionProgress): boolean {
    if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

    if (state.isAdmin || state.isSuperAdmin) return true;

    if (!EmployeeHelper.isSelf(ap.employeeId, state)) return false;

    return [GoalPeriodStatus.SelfAssessment, GoalPeriodStatus.LeaderAssessment].includes(currentPeriod.status);
  }

    public static canDeleteAction(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        if (currentPeriod.status > GoalPeriodStatus.Editing) return false;

        let settings = currentPeriod.reviewPeriod.reviewPeriodGoalSettings;
        let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
            settings.isAllowChangeGoalTextForLeader,
            settings.isAllowChangeGoalTextForSelf,
            settings.isAllowChangeGoalTextForAssistant, currentPeriod, state);

        return hasRight;
    }

    public static canGoalActionSpecFieldsEdit(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        if (GoalPermissionsHelper.isSelfFromPeriod(currentPeriod, state))
        {
            return false;
        }
        else if (currentPeriod.isCurrentUserBossOfTargetEmployee)
        {
            if (currentPeriod.status === GoalPeriodStatus.Editing)
                return true;
        }
        else if (state.isAssistant)
        {
            return false;
        }
    
        return false;
    }

  // KPI

  public static canDeleteKPI(currentPeriod: GoalPeriod, state: State): boolean {
    if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

    if (state.isAdmin || state.isSuperAdmin) return true;
    
    if (currentPeriod.status > GoalPeriodStatus.Editing) return false;

    let settings = currentPeriod.reviewPeriod.reviewPeriodGoalSettings;
    let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
        settings.isAllowChangeGoalTextForLeader,
        settings.isAllowChangeGoalTextForSelf,
        settings.isAllowChangeGoalTextForAssistant, currentPeriod, state);

    return hasRight;
  }

  // Speciális vezérlő mezők szerkesztés jogosultsága, mint pl. minimum elvárt eredmény, súlyozás, stb.
  public static canEditKPISpecFields(currentPeriod: GoalPeriod, state: State): boolean {
    if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;
    
    if (state.isAdmin || state.isSuperAdmin) return true;

    return currentPeriod?.status === GoalPeriodStatus.Editing && currentPeriod.isCurrentUserBossOfTargetEmployee;
  }

  // Group Goals

    public static canGroupGoalDelete(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        if (currentPeriod?.status >= GoalPeriodStatus.SelfAssessment) return false;

        let settings = currentPeriod.reviewPeriod.reviewPeriodGoalSettings;
        let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
            settings.isAllowChangeGoalTextForLeader,
            settings.isAllowChangeGoalTextForSelf,
            settings.isAllowChangeGoalTextForAssistant, currentPeriod, state);

        return hasRight;
    }

    public static canGroupGoalFieldsEdit(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        if (currentPeriod.status != GoalPeriodStatus.Editing) return false;

        return EmployeeHelper.isSelf(currentPeriod.employeeId, state);
    }

    public static canGroupGoalAddNew(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        if (currentPeriod.status != GoalPeriodStatus.Editing) return false;

        return EmployeeHelper.isSelf(currentPeriod.employeeId, state);
    }

    public static canGroupGoalsEdit(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        if (currentPeriod.status != GoalPeriodStatus.Editing) return false;

        return EmployeeHelper.isSelf(currentPeriod.employeeId, state);
    }

    // Goal Panel

    public static canGoalAddNew(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        if (currentPeriod.status > GoalPeriodStatus.Editing) return false;

        let settings = currentPeriod.reviewPeriod.reviewPeriodGoalSettings;

        let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
            settings.isAllowChangeGoalTextForLeader,
            settings.isAllowChangeGoalTextForSelf,
            settings.isAllowChangeGoalTextForAssistant, currentPeriod, state);
    
        return hasRight;
    }
    
    // settings

    public static canAccessSettings(currentPeriod: GoalPeriod, state: State): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        if (state.isAdmin || state.isSuperAdmin) return true;

        let settings = currentPeriod.reviewPeriod.reviewPeriodGoalSettings;

        if (currentPeriod.status > GoalPeriodStatus.LeaderAssessment) return false;

        let hasRight = GoalPermissionsHelper.hasRightBasedOnSettings(
            settings.isAllowSettingsForLeader,
            settings.isAllowSettingsForSelf,
            settings.isAllowSettingsForAssistant,
            currentPeriod, state);

        return hasRight;
    }

    public static allowChangeStatus(currentPeriod: GoalPeriod): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;
        return currentPeriod.reviewPeriod.status !== ReviewPeriodStatusEnum.Closed;
    }

    // Approve
    public static canApprovePeriod(currentPeriod: GoalPeriod, state: State, approverEmployeeId: number): boolean {
        const loggedInEmployee = state?.me.value.employee?.id;

        // date check
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        // az admin userek sem nyúlhatnak itt bele, ők direktben a státuszváltás dropdownban válthatnak státuszt.
        
        if (approverEmployeeId === loggedInEmployee)    //mindenki csak a saját jóváhagyását kezelheti
        {
            if (currentPeriod.employeeId == loggedInEmployee) {  // a bejelentkezett user saját célkitűzése
                if (currentPeriod.status === GoalPeriodStatus.Editing)
                    return true;
                else if (currentPeriod.status === GoalPeriodStatus.SelfAssessment)
                    return true;
                else if (currentPeriod.status === GoalPeriodStatus.LeaderAssessment)
                    return false;
                else if (currentPeriod.status === GoalPeriodStatus.Finished)
                    return true;
            }
            else if (currentPeriod.isCurrentUserBossOfTargetEmployee) { // a bejelentkezett user a főnöke a célkitűzés tulajdonosának
                if (currentPeriod.status === GoalPeriodStatus.Editing)
                    return true;
                else if (currentPeriod.status === GoalPeriodStatus.SelfAssessment)
                    return false;
                else if (currentPeriod.status === GoalPeriodStatus.LeaderAssessment)
                    return true;
                else if (currentPeriod.status === GoalPeriodStatus.Finished)
                    return true;
            }

            if (currentPeriod?.status !== GoalPeriodStatus.Finished) return false;

        }
        else
            return false;
    }

    // 360 selector
    public static canSelectCompetencyResult(currentPeriod: GoalPeriod): boolean {
        if (!GoalPermissionsHelper.isAllowByPeriod(currentPeriod)) return false;

        return currentPeriod.status < GoalPeriodStatus.Approved;
    }
}